import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const HistoryCalendar = ({ history, onSelectSnapshot }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const runDates = useMemo(() => {
    const map = {};
    (history || []).forEach(h => {
      const d = new Date(h.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(h);
    });
    return map;
  }, [history]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  let startDay = firstDayOfMonth.getDay() - 1;
  if (startDay < 0) startDay = 6;

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const handleDayClick = (day) => {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const snapshots = runDates[key];
    if (snapshots && snapshots.length > 0) {
      setSelectedDate(key);
      onSelectSnapshot(snapshots[snapshots.length - 1], key);
    }
  };

  const calendarDays = [];
  for (let i = 0; i < startDay; i++) {
    calendarDays.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push(d);
  }

  if (!history || history.length === 0) return null;

  return (
    <div className="bg-white border border-[#E2DDD8] mb-12" data-testid="history-calendar">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid="calendar-toggle-button"
      >
        <div className="flex items-center gap-3">
          <CalendarDays className="w-5 h-5 text-[#1BB594]" />
          <span className="text-lg font-bold text-slate-900">{'Monitoring History'}</span>
          <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 bg-slate-50 text-slate-600 border border-slate-200">
            {Object.keys(runDates).length} {'days'}
          </span>
        </div>
        <span className="text-sm text-slate-400">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="px-6 pb-6 border-t border-[#E2DDD8] pt-6">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 hover:bg-slate-100 transition-colors" data-testid="calendar-prev-month">
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <span className="text-lg font-bold text-slate-900">
              {MONTH_NAMES[month]} {year}
            </span>
            <button onClick={nextMonth} className="p-2 hover:bg-slate-100 transition-colors" data-testid="calendar-next-month">
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-xs font-bold uppercase tracking-wider text-slate-400 py-2">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, idx) => {
              if (!day) {
                return <div key={`empty-${idx}`} className="h-10" />;
              }
              const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const hasRun = !!runDates[key];
              const isSelected = selectedDate === key;

              return (
                <button
                  key={day}
                  onClick={() => handleDayClick(day)}
                  disabled={!hasRun}
                  className={`h-10 text-sm font-semibold transition-colors relative ${
                    isSelected
                      ? 'bg-[#1BB594] text-white'
                      : hasRun
                        ? 'bg-[#1BB594]/10 text-[#1BB594] hover:bg-[#1BB594]/20 cursor-pointer'
                        : 'text-slate-300 cursor-default'
                  }`}
                  data-testid={`calendar-day-${day}`}
                >
                  {day}
                  {hasRun && (
                    <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                      isSelected ? 'bg-white' : 'bg-[#1BB594]'
                    }`} />
                  )}
                </button>
              );
            })}
          </div>

          {selectedDate && runDates[selectedDate] && (
            <div className="mt-6 pt-6 border-t border-[#E2DDD8]" data-testid="calendar-snapshot">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                SNAPSHOT FOR {selectedDate}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {Object.entries(runDates[selectedDate][runDates[selectedDate].length - 1].ai_engine_results || {}).map(([engine, data]) => (
                  <div key={engine} className="p-3 bg-slate-50 border border-slate-200 text-center">
                    <p className="text-xs font-bold text-slate-500 mb-1">{engine}</p>
                    <p className="text-lg font-black text-slate-900">{data.visibility_percent}%</p>
                    <p className="text-xs text-slate-400">cit. {data.citation_percent}%</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-center">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-500">SOV:</span>
                <span className="text-2xl font-black text-[#1BB594] ml-2">
                  {runDates[selectedDate][runDates[selectedDate].length - 1].sov_overall}%
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryCalendar;
